import { useCallback } from 'react'
import { Strophe } from 'react-strophe'
import { useUser } from '@fs/zion-user'
import orchestrationApi from '../api/orchestration'
import { useChatState } from '../state/ChatState'
import { isChatRoom, isPrivateChatRoom } from '../global/build-jid'
import useDataEndpoint from './useDataEndpoint'
import { Chat } from '../global/chatSettingsConfig'
import useRequestState from './useRequestState'

export const useBookmarkList = () => {
  const { bookmarks, refetchBookmarks } = useChatState()
  return [bookmarks, refetchBookmarks]
}

export const useMyConversationsList = () => {
  const { roster = [], bookmarks = [] } = useChatState()
  const privateChats = bookmarks?.filter((bookmark) => isPrivateChatRoom(bookmark.jid)) || []

  const conversations = [...roster, ...privateChats]
  return conversations.map((item) => {
    return {
      ...item,
      jid: item.jid.includes(Chat.Settings.DOMAIN)
        ? item.jid.toLowerCase()
        : `${item.jid.toLowerCase()}@${Chat.Settings.DOMAIN}`,
    }
  })
}

export const useExpertData = ({ cisUserId }) => {
  const getExpertData = useCallback(() => {
    return cisUserId
      ? orchestrationApi.support.getSupportExpertInfo({ cisUserId }).then(({ data }) => data)
      : Promise.resolve(null)
  }, [cisUserId])
  return useDataEndpoint(getExpertData)
}

export const useParticipantCount = (jid) => {
  const fn = useCallback(() => {
    return jid && isChatRoom(jid)
      ? orchestrationApi.bookmarks.getParticipantsCountWithBookmark({ jid }).then(({ data }) => data)
      : Promise.resolve(null)
  }, [jid])

  return useDataEndpoint(fn)
}

export const useGetChatRoom = ({ jid, description, lookupIfNoDescription = true }) => {
  const getChatRoom = useCallback(() => {
    const isGroup = jid && jid.includes(Chat.Settings.MUC_DOMAIN) && !isPrivateChatRoom(jid)
    const shouldLookup = isGroup && lookupIfNoDescription && !description
    return isGroup && shouldLookup
      ? orchestrationApi.chatgroups.getChatRoom({ jid: Strophe.getNodeFromJid(jid) }).then(({ data }) => data)
      : Promise.resolve()
  }, [description, jid, lookupIfNoDescription])

  return useDataEndpoint(getChatRoom)
}

export const useGetBookmarkDetailsByJids = ({ jids }) => {
  const getDetailsOfBookmarkJids = useCallback(() => {
    return orchestrationApi.bookmarks.getBookmarkDetailsByBulk({ jids })
  }, [jids])

  return useDataEndpoint(getDetailsOfBookmarkJids)
}

export const useGetChildrenChatRooms = ({ parentJid }) => {
  const getChildren = useCallback(async () => {
    if (parentJid) {
      try {
        const validJid = parentJid.includes(Chat.Settings.MUC_DOMAIN)
          ? parentJid
          : `${parentJid}@${Chat.Settings.MUC_DOMAIN}`
        return await orchestrationApi.chatgroups.getChildrenChatRooms({ parentJid: validJid })
      } catch {
        return []
      }
    }

    return null
  }, [parentJid])

  return useDataEndpoint(getChildren)
}

export const useTopChatRooms = () => {
  const { connection } = useChatState()
  const results = useRequestState(async () => {
    try {
      return await orchestrationApi.bookmarks.getTopChatRooms()
    } catch {
      return []
    }
  })
  return connection ? results : []
}

export const useSendChatErrorToSplunk = () => {
  const NOT_SET = 'not-set'
  const fsUser = useUser()
  const { connection, user } = useChatState()
  const jabberResource = connection?.jid ? Strophe.getResourceFromJid(connection.jid) : NOT_SET
  const displayName = fsUser.displayName || NOT_SET
  const jid1 = user.authzid || `${fsUser.cisId || NOT_SET}@${Chat.Settings.DOMAIN}`.toLowerCase()

  const sendError = useCallback(
    (error) => {
      // fire and forget
      try {
        orchestrationApi.chatmetrics.logUnableToConnectError({ jabberResource, jid1, displayName, errorName: error })
      } catch (e) {
        // ignoring
      }
    },
    [jabberResource, jid1, displayName]
  )

  return [sendError]
}

export const useSendAskUsAnythingMetricToSplunk = () => {
  const { connection, user } = useChatState()
  const fsUser = useUser()
  const jabberResource = Strophe.getResourceFromJid(connection.jid)
  const fromJid = user.authzid
  const displayName = fsUser.displayName

  const sendMetric = useCallback(
    ({ topic, toJid }) => {
      // fire and forget
      try {
        orchestrationApi.chatmetrics.logAskUsAnythingMetric({ jabberResource, toJid, fromJid, displayName, topic })
      } catch (e) {
        // ignoring
      }
    },
    [displayName, fromJid, jabberResource]
  )
  return [sendMetric]
}

export const useChatWithRelativeMetricToSplunk = () => {
  const { connection, user } = useChatState()
  const jabberResource = Strophe.getResourceFromJid(connection.jid)
  const fromJid = user.authzid

  const sendMetric = useCallback(
    ({ relativeJid }) => {
      // fire and forget
      try {
        orchestrationApi.chatmetrics.logStartChatWithRelative({ jabberResource, jid: fromJid, relativeJid })
      } catch (e) {
        // ignoring
      }
    },
    [fromJid, jabberResource]
  )
  return [sendMetric]
}
